import { useMutation } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";

import { useCMSStore } from "@/hooks/use-cms-store";
import { useDialogState } from "@/hooks/use-dialog-state";
import { AnalyticsEvent, trackEvent } from "@/lib/analytics";
import client from "@/lib/client";
import { removeAllColumnMappings } from "@/lib/cms";
import { LGT_SERVER_URL } from "@/lib/env";
import {
  uploadUniqueContentCSV,
  UploadUniqueContentCSVOptions,
} from "@/lib/uploads";
import useStore from "@/state";

import {
  createCustomVerificationType,
  createOrganization,
  createSKU,
  deleteCustomVerificationType,
  deleteImage,
  deleteOrganization,
  deleteSKU,
  deleteTag,
  deleteTrack,
  deleteVideo,
  deployContract,
  exportImages,
  exportOwners,
  exportSKUs,
  exportTags,
  exportTagVerifications,
  exportTracks,
  exportVideos,
  forceMetadataUpdate,
  forceMint,
  getAuditLogs as requestAuditLogs,
  resequenceSKU,
  resetSKUUniqueContent,
  transferSKU,
  transferTags,
  updateImage,
  updateOrganizationSettings,
  updateSKU,
  updateVideo,
} from "./api";
import { RequestService } from "./requests";
import {
  CustomVerificationType,
  DeployContractArgs,
  Image,
  ListQueryArgs,
  OrganizationSettings,
  SKU,
  TransferTagsBody,
  Video,
} from "./types";

export const useCreateSKUMutation = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationKey: ["skus", "create"],
    mutationFn: async (params: Partial<SKU>) => {
      return createSKU(params);
    },
    onSuccess: (data) => {
      trackEvent(AnalyticsEvent.SKU_CREATED, {
        sku_id: data.id,
        sku_name: data.name,
      });
      client.invalidateQueries({ queryKey: ["skus"] });
      toast.success("Created SKU", {
        description: `SKU ${data.id} created successfully.`,
      });
      navigate(`/skus/${data.id}`);
    },
    onError: (error) => {
      toast.error("Failed to create SKU", {
        description: error.message,
      });
    },
  });
};

export const useUpdateSKUMutation = (skuId: string) => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["skus", "update", skuId],
    mutationFn: async (params: Partial<SKU>) => {
      return updateSKU(skuId, params);
    },
    onSuccess: (data) => {
      trackEvent(AnalyticsEvent.SKU_EDITED, {
        sku_id: data.id,
        sku_name: data.name,
      });
      client.invalidateQueries({ queryKey: ["skus", String(data.id)] });
      toast.success("Changes saved", {
        description: `SKU ${data.id} updated successfully.`,
      });
      closeDialog();
    },
    onError: (event) => {
      toast.error("Failed to update SKU", {
        description: event.message,
      });
    },
  });
};

export const useDeleteSKUMutation = () => {
  const navigate = useNavigate();
  const { dialog, closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["skus", "delete"],
    mutationFn: async (skuId: string) => {
      return deleteSKU(skuId);
    },
    onSuccess: () => {
      trackEvent(AnalyticsEvent.SKU_DELETED);
      client.invalidateQueries({ queryKey: ["skus"] });
      toast.success("Deleted SKU", {
        description: "SKU has been successfully deleted.",
      });
      navigate("/skus");
    },
    onSettled: () => {
      if (dialog?.name === "delete-sku") {
        closeDialog();
      }
    },
    onError: (error) => {
      toast.error("Failed to delete SKU", {
        description: error.message,
      });
    },
  });
};

export const useTransferSKUMutation = () => {
  const navigate = useNavigate();
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["skus", "transfer"],
    mutationFn: async ({
      skuId,
      workOSOrgId,
    }: {
      skuId: string;
      workOSOrgId: string;
    }) => {
      return transferSKU(skuId, workOSOrgId);
    },
    onSuccess: () => {
      toast.success("Transferred SKU", {
        description: "SKU has been successfully transferred.",
      });
      client.invalidateQueries({ queryKey: ["skus"] });
      navigate("/skus");
    },
    onSettled: () => {
      closeDialog();
    },
    onError: (error) => {
      toast.error("Failed to transfer SKU", {
        description: error.message,
      });
    },
  });
};

export const useResequenceSKUMutation = () => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["skus", "resequence"],
    mutationFn: async ({ skuId }: { skuId: string }) => {
      return resequenceSKU(skuId);
    },
    onSuccess: () => {
      toast.success("Resequenced SKU", {
        description: "Tags in SKU have been successfully resequenced.",
      });
      client.invalidateQueries({ queryKey: ["skus"] });
      client.invalidateQueries({ queryKey: ["tags"] });
    },
    onSettled: () => {
      closeDialog();
    },
    onError: (error) => {
      toast.error("Failed to resequence tags in SKU", {
        description: error.message,
      });
    },
  });
};

export const useDeleteTagMutation = () => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["tags", "delete"],
    mutationFn: async (tagId: string) => {
      await deleteTag(tagId);

      return { tagId };
    },
    onSuccess: (data) => {
      client.invalidateQueries({ queryKey: ["tags"] });
      toast.success("Deleted Tag", {
        description: `Tag ${data.tagId} has been successfully deleted`,
      });
    },
    onSettled: () => {
      closeDialog();
    },
    onError: (error) => {
      toast.error("Failed to delete tag", {
        description: error.message,
      });
    },
  });
};

export const useTransferTagsMutation = (templateId: string) => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["tags", "transfer"],
    mutationFn: async (body: TransferTagsBody) => {
      return transferTags(body);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["skus", templateId] });
      toast.success("Moved Tags", {
        description: "Tags have been successfully moved",
      });
    },
    onError: () => {
      toast.error("Failed to move tags", {
        description: "An error occurred while moving tags",
      });
    },
    onSettled: () => {
      closeDialog();
    },
  });
};

export const useCreateOrganizationMutation = () => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["organizations", "create"],
    mutationFn: (name: string) => {
      return createOrganization(name);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["organizations"] });
      client.invalidateQueries({ queryKey: ["organization-memberships"] });
      toast.success("Created organization", {
        description: "New organization successfully created",
      });
    },
    onSettled: () => {
      closeDialog();
    },
    onError: (error) => {
      toast.error("Failed to create organization", {
        description: error.message,
      });
    },
  });
};

export const useDeleteOrganizationMutation = () => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["organizations", "delete"],
    mutationFn: (workOSOrganizationId: string) => {
      return deleteOrganization(workOSOrganizationId);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["organizations"] });
      toast.success("Deleted Organization", {
        description: "Organization has been successfully deleted",
      });
    },
    onSettled: () => {
      closeDialog();
    },
    onError: (error) => {
      toast.error("Failed to delete organization", {
        description: error.message,
      });
    },
  });
};

export const useUpdateOrganizationSettingsMutation = () => {
  return useMutation({
    mutationKey: ["organizations", "update-settings"],
    mutationFn: (settings: OrganizationSettings) => {
      return updateOrganizationSettings(settings);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["organization"] });
      toast.success("Organization settings updated", {
        description: "Organization settings have been successfully updated.",
      });
    },
    onError: (error) => {
      toast.error("Failed to update organization settings", {
        description: error.message,
      });
    },
  });
};

export const useExportSKUsMutation = ({ defaultSort }: ListQueryArgs = {}) => {
  const [searchParams] = useSearchParams();

  const limit = searchParams.get("limit");
  const page = searchParams.get("page");
  const sort = searchParams.get("sort") ?? defaultSort?.column;
  const direction = searchParams.get("direction") ?? defaultSort?.direction;

  const search = searchParams.get("search");

  return useMutation({
    mutationKey: ["skus", "export"],
    mutationFn: async () => {
      const response = await exportSKUs({
        search,
        page: Number(page) || 1,
        limit: Number(limit) || 20,
        sort,
        direction,
      });
      const href = window.URL.createObjectURL(response);

      const element = document.createElement("a");
      element.href = href;

      element.download = `skus_${new Date().toISOString()}.csv`;
      document.body.appendChild(element);
      element.click();
      element.remove();
    },
    onSuccess: () => {
      trackEvent(AnalyticsEvent.SKU_CSV_DOWNLOADED);
      toast.success("Downloaded CSV", {
        description: `SKUs have been downloaded`,
      });
    },
    onError: (error) => {
      toast.error("Failed to export SKU", {
        description: error.message,
      });
    },
  });
};

export const useExportTagsMutation = ({ defaultSort }: ListQueryArgs = {}) => {
  const [searchParams] = useSearchParams();

  const page = searchParams.get("page");
  const limit = searchParams.get("limit");
  const sort = searchParams.get("sort") || defaultSort?.column;
  const direction = searchParams.get("direction") || defaultSort?.direction;

  const templateId = searchParams.get("templateId");
  const uid = searchParams.get("uid");

  return useMutation({
    mutationKey: ["tags", "export"],
    mutationFn: async () => {
      const response = await exportTags({
        template_id: templateId,
        uid,
        page: Number(page) || 1,
        limit: Number(limit) || 20,
        sort,
        direction,
      });
      const href = window.URL.createObjectURL(response);

      const element = document.createElement("a");
      element.href = href;

      element.download = `tags_${new Date().toISOString()}.csv`;
      document.body.appendChild(element);
      element.click();
      element.remove();
    },
    onSuccess: () => {
      toast.success("Downloaded CSV", {
        description: `Tags have been downloaded`,
      });
    },
    onError: (error) => {
      toast.error("Failed to export tags", {
        description: error.message,
      });
    },
  });
};

export const useExportTagVerificationsMutation = ({
  defaultSort,
}: ListQueryArgs = {}) => {
  const [searchParams] = useSearchParams();

  const limit = searchParams.get("limit");
  const page = searchParams.get("page");
  const sort = searchParams.get("sort") ?? defaultSort?.column;
  const direction = searchParams.get("direction") ?? defaultSort?.direction;

  const templateId = searchParams.get("templateId");
  const uid = searchParams.get("uid");
  const verifyType = searchParams.get("verifyType");
  const customVerificationTypeId = searchParams.get("customVerificationTypeId");

  return useMutation({
    mutationKey: ["verifications", "export"],
    mutationFn: async () => {
      const response = await exportTagVerifications({
        template_id: templateId,
        uid,
        verify_type: verifyType,
        custom_verification_type_id: customVerificationTypeId,
        page: Number(page) || 1,
        limit: Number(limit) || 20,
        sort,
        direction,
      });
      const href = window.URL.createObjectURL(response);

      const element = document.createElement("a");
      element.href = href;

      element.download = `verifications_${new Date().toISOString()}.csv`;
      document.body.appendChild(element);
      element.click();
      element.remove();
    },
    onSuccess: () => {
      toast.success("Downloaded CSV", {
        description: `Tag verifications have been downloaded`,
      });
    },
    onError: (error) => {
      toast.error("Failed to export tag verifications", {
        description: error.message,
      });
    },
  });
};

export const useExportOwnersMutation = () => {
  return useMutation({
    mutationKey: ["owners", "export"],
    mutationFn: async (templateId: string) => {
      const response = await exportOwners(templateId);
      const href = window.URL.createObjectURL(response);

      const element = document.createElement("a");
      element.href = href;

      element.download = `sku_${templateId}_owners_by_tag_id_${new Date().toISOString()}.csv`;
      document.body.appendChild(element);
      element.click();
      element.remove();

      return { templateId };
    },
    onSuccess: (data) => {
      toast.success("Downloaded CSV", {
        description: `Owners have been downloaded for SKU ${data.templateId}`,
      });
    },
    onError: (error) => {
      toast.error("Failed to export owners", {
        description: error.message,
      });
    },
  });
};

export const useExportImagesMutation = ({
  defaultSort,
}: ListQueryArgs = {}) => {
  const [searchParams] = useSearchParams();

  const limit = searchParams.get("limit");
  const page = searchParams.get("page");
  const sort = searchParams.get("sort") ?? defaultSort?.column;
  const direction = searchParams.get("direction") ?? defaultSort?.direction;

  const search = searchParams.get("search");

  return useMutation({
    mutationKey: ["images", "export"],
    mutationFn: async () => {
      const response = await exportImages({
        search,
        page: Number(page) || 1,
        limit: Number(limit) || 20,
        sort,
        direction,
      });
      const href = window.URL.createObjectURL(response);

      const element = document.createElement("a");
      element.href = href;

      element.download = `images_${new Date().toISOString()}.csv`;
      document.body.appendChild(element);
      element.click();
      element.remove();
    },
    onSuccess: () => {
      toast.success("Downloaded CSV", {
        description: `Images have been downloaded`,
      });
    },
    onError: (error) => {
      toast.error("Failed to export images", {
        description: error.message,
      });
    },
  });
};

export const useExportVideosMutation = ({
  defaultSort,
}: ListQueryArgs = {}) => {
  const [searchParams] = useSearchParams();

  const limit = searchParams.get("limit");
  const page = searchParams.get("page");
  const sort = searchParams.get("sort") ?? defaultSort?.column;
  const direction = searchParams.get("direction") ?? defaultSort?.direction;

  const search = searchParams.get("search");

  return useMutation({
    mutationKey: ["videos", "export"],
    mutationFn: async () => {
      const response = await exportVideos({
        search,
        page: Number(page) || 1,
        limit: Number(limit) || 20,
        sort,
        direction,
      });
      const href = window.URL.createObjectURL(response);

      const element = document.createElement("a");
      element.href = href;

      element.download = `videos_${new Date().toISOString()}.csv`;
      document.body.appendChild(element);
      element.click();
      element.remove();
    },
    onSuccess: () => {
      toast.success("Downloaded CSV", {
        description: `Videos have been downloaded`,
      });
    },
    onError: (error) => {
      toast.error("Failed to export videos", {
        description: error.message,
      });
    },
  });
};

export const useExportTracksMutation = ({
  defaultSort,
}: ListQueryArgs = {}) => {
  const [searchParams] = useSearchParams();

  const limit = searchParams.get("limit");
  const page = searchParams.get("page");
  const sort = searchParams.get("sort") ?? defaultSort?.column;
  const direction = searchParams.get("direction") ?? defaultSort?.direction;

  const search = searchParams.get("search");

  return useMutation({
    mutationKey: ["tracks", "export"],
    mutationFn: async () => {
      const response = await exportTracks({
        search,
        page: Number(page) || 1,
        limit: Number(limit) || 20,
        sort,
        direction,
      });
      const href = window.URL.createObjectURL(response);

      const element = document.createElement("a");
      element.href = href;

      element.download = `tracks_${new Date().toISOString()}.csv`;
      document.body.appendChild(element);
      element.click();
      element.remove();
    },
    onSuccess: () => {
      toast.success("Downloaded CSV", {
        description: `Tracks have been downloaded`,
      });
    },
    onError: (error) => {
      toast.error("Failed to export tracks", {
        description: error.message,
      });
    },
  });
};

export const useDeployContractMutation = () => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["skus", "deploy-contract"],
    mutationFn: async (args: DeployContractArgs) => {
      return deployContract(args);
    },
    onSuccess: (data) => {
      trackEvent(AnalyticsEvent.BLOCKCHAIN_ENABLED, {
        sku_id: data.id,
        sku_name: data.name,
        chain_id: data.nft_chain_id ?? null,
      });
      client.invalidateQueries({ queryKey: ["skus", String(data.id)] });
      toast.success("Contract deployed", {
        description: "Contract has been successfully deployed",
      });
    },
    onError: () => {
      toast.error("Failed to deploy contract", {
        description: "Failed to deploy contract",
      });
    },
    onSettled: () => {
      closeDialog();
    },
  });
};

export const useForceMetadataUpdateMutation = () => {
  return useMutation({
    mutationKey: ["skus", "force-metadata-update"],
    mutationFn: async (skuId: string) => {
      return forceMetadataUpdate(skuId);
    },
    onSuccess: () => {
      // TODO: Improve server response to narrow SKU down.
      client.invalidateQueries({ queryKey: ["skus"] });
      toast.success("Forced metadata update", {
        description: "Metadata has been successfully updated",
      });
    },
    onError: (error) => {
      toast.error("Failed to force metadata update", {
        description: error.message,
      });
    },
  });
};

export const useForceMintMutation = () => {
  return useMutation({
    mutationKey: ["skus", "force-mint"],
    mutationFn: async (skuId: string) => {
      return forceMint(skuId);
    },
    onSuccess: () => {
      // TODO: Improve server response to narrow SKU down.
      client.invalidateQueries({ queryKey: ["skus"] });
      toast.success("Forced mint", {
        description: "NFTs will be minted shortly.",
      });
    },
    onError: (error) => {
      toast.error("Failed to force mint", {
        description: error.message,
      });
    },
  });
};

export const useRequestAuditLogsMutation = () => {
  return useMutation({
    mutationKey: ["audit-logs", "request"],
    mutationFn: () => {
      return requestAuditLogs();
    },
    onSuccess: (data) => {
      trackEvent(AnalyticsEvent.AUDIT_LOG_REQUESTED, {
        sku_id: data.id,
        sku_name: data.state,
        requested_log_url: data.url,
      });
      toast.success("Requested audit logs", {
        description: "Audit logs will be available shortly.",
      });
    },
    onError: (error) => {
      toast.error("Failed to request audit logs", {
        description: error.message,
      });
    },
  });
};

export const useRefreshTokenMutation = () => {
  const navigate = useNavigate();
  const { setSession } = useStore();

  return useMutation({
    mutationKey: ["refresh-token"],
    mutationFn: async (organizationId: string) => {
      const requests = new RequestService();

      const token = await requests.refreshToken(organizationId);

      // FIXME: This portion is copied from the useAuthCallbackQuery hook.
      // We should standardize it to make sure the behaviors do not diverge.
      const sessionResponse = await fetch(`${LGT_SERVER_URL}/auth/session`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      // Session expired / invalid
      if (sessionResponse.status === 401) {
        setSession(null);
        window.localStorage.removeItem("token");
        throw new Error("Session expired");
      }

      if (!sessionResponse.ok) {
        throw new Error(
          `Failed to fetch session: ${sessionResponse.status} ${sessionResponse.statusText}`,
        );
      }

      const session = await sessionResponse.json();

      setSession(session);

      return token;
    },
    onSuccess: () => {
      navigate("/skus", { replace: true });
      client.invalidateQueries({ queryKey: ["skus"] });
    },
    onError: (error) => {
      setSession(null);
      window.localStorage.removeItem("token");
      navigate("/", { replace: true });
      toast.error("Failed to authenticate existing login session", {
        description: error.message,
      });
    },
  });
};

export const useCreateCustomVerificationTypeMutation = () => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["custom-verification-types", "create"],
    mutationFn: (body: Partial<CustomVerificationType>) => {
      return createCustomVerificationType(body);
    },
    onSuccess: (data) => {
      trackEvent(AnalyticsEvent.CUSTOM_VERIFICATION_TYPE_CREATED, {
        sku_id: data.id,
        sku_name: data.name,
      });
      client.invalidateQueries({ queryKey: ["custom-verification-types"] });
      toast.success("Created Verification Type", {
        description: "New verification type successfully created",
      });
    },
    onSettled: () => {
      closeDialog();
    },
    onError: (error) => {
      toast.error("Failed to create custom verification type", {
        description: error.message,
      });
    },
  });
};

export const useDeleteCustomVerificationTypeMutation = () => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["custom-verification-types", "delete"],
    mutationFn: (verificationTypeId: number) => {
      return deleteCustomVerificationType(verificationTypeId);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["custom-verification-types"] });
      toast.success("Deleted Verification Type", {
        description: "Verification type has been successfully deleted",
      });
    },
    onError: (error) => {
      toast.error("Failed to delete custom verification type", {
        description: error.message,
      });
    },
    onSettled: () => {
      closeDialog();
    },
  });
};

export const useEditVideoMutation = () => {
  const { dialog, closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["videos", "update"],
    mutationFn: async (video: Partial<Video>) => {
      return updateVideo(video);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["videos"] });
      toast.success("Video Updated", {
        description: "Video has been successfully updated.",
      });
    },
    onSettled: () => {
      if (dialog?.name === "update-video") {
        closeDialog();
      }
    },
    onError: (error) => {
      toast.error("Failed to update video", {
        description: error.message,
      });
    },
  });
};

export const useDeleteVideoMutation = () => {
  const { dialog, closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["videos", "delete"],
    mutationFn: async (id: number) => {
      return deleteVideo(id);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["videos"] });
      toast.success("Video Deleted", {
        description: "Video has been successfully deleted.",
      });
    },
    onSettled: () => {
      if (dialog?.name === "delete-video") {
        closeDialog();
      }
    },
    onError: (error) => {
      toast.error("Failed to delete video", {
        description: error.message,
      });
    },
  });
};

export const useEditImageMutation = () => {
  const { dialog, closeDialog } = useDialogState();
  return useMutation({
    mutationKey: ["images", "update"],
    mutationFn: async (image: Partial<Image>) => {
      return updateImage(image);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["images"] });
      toast.success("Image Updated", {
        description: "Image has been successfully updated.",
      });
    },
    onSettled: () => {
      if (dialog?.name === "update-image") {
        closeDialog();
      }
    },
    onError: (error) => {
      toast.error("Failed to update image", {
        description: error.message,
      });
    },
  });
};

export const useDeleteImageMutation = () => {
  const { dialog, closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["images", "delete"],
    mutationFn: async (id: number) => {
      return deleteImage(id);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["images"] });
      toast.success("Image Deleted", {
        description: "Image has been successfully deleted.",
      });
    },
    onSettled: () => {
      if (dialog?.name === "delete-image") {
        closeDialog();
      }
    },
    onError: (error) => {
      toast.error("Failed to delete image", {
        description: error.message,
      });
    },
  });
};

export const useEditTrackMutation = () => {
  const { dialog, closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["tracks", "update"],
    mutationFn: async (track: Partial<Video>) => {
      return updateVideo(track);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["tracks"] });
      toast.success("Track Updated", {
        description: "Track has been successfully updated.",
      });
    },
    onSettled: () => {
      if (dialog?.name === "update-track") {
        closeDialog();
      }
    },
    onError: (error) => {
      toast.error("Failed to delete track", {
        description: error.message,
      });
    },
  });
};

export const useDeleteTrackMutation = () => {
  const { dialog, closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["tracks", "delete"],
    mutationFn: async (id: number) => {
      return deleteTrack(id);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["tracks"] });
      toast.success("Track Deleted", {
        description: "Track has been successfully deleted.",
      });
    },
    onSettled: () => {
      if (dialog?.name === "delete-track") {
        closeDialog();
      }
    },
    onError: (error) => {
      toast.error("Failed to delete track", {
        description: error.message,
      });
    },
  });
};

export const useUpdateUniqueContentMutation = (skuId: string) => {
  return useMutation({
    mutationKey: ["skus", skuId, "update-unique-data"],
    mutationFn: async (options: UploadUniqueContentCSVOptions) => {
      return uploadUniqueContentCSV(options);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["skus", skuId] });

      toast.success("Unique content updated", {
        description: "Unique content has been successfully updated.",
      });
    },
    onError: (error) => {
      toast.error("Failed to update unique data", {
        description: error.message,
      });
    },
  });
};

export const useResetUniqueContentMutation = (skuId: string) => {
  const setPageContent = useCMSStore.use.setPageContent();

  return useMutation({
    mutationKey: ["skus", skuId, "reset-unique-content"],
    mutationFn: async () => {
      // We have to reset the unique content and update the page content
      // separately because the endpoints are different.

      const pageContent = useCMSStore.getState().pageContent;
      if (!pageContent) {
        throw new Error("Page content is required");
      }

      // This is sequential to avoid a race condition on SKU data invalidation.
      await resetSKUUniqueContent(skuId);
      const updatedSKU = await updateSKU(skuId, {
        page_content: removeAllColumnMappings(pageContent),
      });

      return updatedSKU;
    },
    onSuccess: (sku) => {
      client.invalidateQueries({ queryKey: ["skus", skuId] });

      // Keep page content in sync, no unsaved changes.
      setPageContent(sku.page_content);

      toast.success("Unique content reset", {
        description: "Unique content has been successfully reset.",
      });
    },
    onError: (error) => {
      toast.error("Failed to reset unique content", {
        description: error.message,
      });
    },
  });
};
